import { useEffect, useMemo, useState } from 'react';
import { Bell, Plus, RefreshCw, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { createNotification, listNotifications, NotificationItem } from '../services/notificationsService';

const Notifications = () => {
  const { isSuperAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await listNotifications({ limit: 50, offset: 0 });
      setItems(result.notifications || []);
    } catch (err) {
      console.error(err);
      const detail = err instanceof Error ? err.message : '';
      setError(detail ? `Impossibile caricare le notifiche: ${detail}` : 'Impossibile caricare le notifiche.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const counters = useMemo(() => {
    return items.reduce(
      (acc, it) => {
        const st = String(it.status || '').toLowerCase();
        if (st === 'sent') acc.sent += 1;
        else if (st === 'send_failed') acc.failed += 1;
        else acc.draft += 1;
        return acc;
      },
      { sent: 0, draft: 0, failed: 0 },
    );
  }, [items]);

  const onSend = async () => {
    if (!title.trim() || !message.trim()) {
      setError('Titolo e messaggio sono obbligatori.');
      return;
    }

    try {
      setBusy(true);
      setError(null);
      setSuccess(null);

      const result = await createNotification({
        title: title.trim(),
        message: message.trim(),
        send: true,
      });

      setSuccess(result.status === 'sent' ? `Notifica inviata (ID: ${result.notificationId}).` : `Notifica salvata (ID: ${result.notificationId}).`);
      setTitle('');
      setMessage('');
      await load();
      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      console.error(err);
      const detail = err instanceof Error ? err.message : '';
      setError(detail ? `Invio non riuscito: ${detail}` : 'Invio non riuscito.');
    } finally {
      setBusy(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="card p-8 max-w-3xl">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2 mb-2">
          <Bell size={24} className="text-primary" />
          Notifiche
        </h1>
        <p className="text-sm text-text-secondary">Questa sezione e riservata al superadmin.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
            <Bell size={30} className="text-primary" />
            Notifiche (Superadmin)
          </h1>
          <p className="text-text-secondary mt-1">
            Invia aggiornamenti push all’app mobile.
          </p>
        </div>
        <button type="button" className="btn-outline inline-flex items-center gap-2" onClick={() => void load()} disabled={loading}>
          <RefreshCw size={15} /> Aggiorna
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <p className="text-xs text-text-secondary">Inviate</p>
          <p className="text-2xl font-bold text-green-600">{counters.sent}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-text-secondary">Bozze</p>
          <p className="text-2xl font-bold text-text-primary">{counters.draft}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-text-secondary">Fallite</p>
          <p className="text-2xl font-bold text-red-600">{counters.failed}</p>
        </div>
      </div>

      {error && <div className="rounded-lg border border-error/30 bg-error/10 p-4 text-error">{error}</div>}
      {success && <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-green-700">{success}</div>}

      <section className="card p-5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-xl font-semibold text-text-primary inline-flex items-center gap-2">
            <Plus size={18} /> Nuova notifica
          </h2>
        </div>

        <label className="block">
          <span className="block text-sm font-medium text-text-secondary mb-2">Titolo</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-lg border border-border px-3 py-2"
            placeholder="Es. Nuovo evento in arrivo"
          />
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-text-secondary mb-2">Messaggio</span>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full rounded-lg border border-border px-3 py-2 min-h-28"
            placeholder="Scrivi il testo della notifica push..."
          />
        </label>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            onClick={() => void onSend()}
            disabled={busy}
          >
            <Send size={16} /> {busy ? 'Invio...' : 'Invia push'}
          </button>
        </div>
      </section>

      <section className="card p-5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-xl font-semibold text-text-primary">Storico</h2>
          <p className="text-sm text-text-secondary">{items.length} elementi</p>
        </div>

        {loading ? (
          <div className="text-text-secondary">Caricamento...</div>
        ) : items.length === 0 ? (
          <div className="text-text-secondary">Nessuna notifica trovata.</div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <article key={it.id} className="rounded-xl border border-border bg-background p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-text-primary">{it.title}</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-text-primary">
                    {it.status || 'draft'}
                  </span>
                  <span className="text-xs text-text-secondary ml-auto">
                    {it.createdAt ? new Date(it.createdAt).toLocaleString('it-IT') : 'n/d'}
                  </span>
                </div>
                <p className="text-sm text-text-secondary mt-2 whitespace-pre-wrap">{it.message}</p>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Notifications;

